// The sea objects in this viewer's picture, as the client keeps them
// (v2-the-deep.md §11.9, §14.9, R2654, R2657).
//
// A State frame carries the sea objects as deltas, by the rule of the
// fleets: a row goes out when it is new to this client or one of its
// fields changed (`State.objects`), the id of a row that left the picture
// rides `State.objects_gone`, and a full frame restates every row. Silence
// means "unchanged", not "gone".
//
// A row is KNOWN or HEARD. A known row carries the kind, the arm and the
// hull (R2894). A heard row is a sea mine that a hull at sea of the viewer
// alone hears (R2902): it carries the id, the faction and the point, its
// kind reads `SEA_OBJECT_KIND_NONE` here, and the map draws the generic
// mine mark. A known row of another faction carries the target bit of this
// viewer: a gun of the viewer fires at it, and the map draws the target
// bracket round it. A heard row is never a target.
//
// The store also keeps the Sapper fields of each own Sapper that the fleet
// rows carry: the stow aboard, the map seconds until the next LAY and the
// map seconds the loud lay still runs. The wire re-sends a timer only at a
// change of its 5 s bucket (R2594), so the store counts it down between two
// frames at the tactical scale, as `guards.ts` does.
//
// The store answers the three moments the canvas draws a look for, from
// the rows alone, with no event on the wire (R2657):
//
// - a LAY: a new own row that a delta (not a full frame) carries inside
//   `lay_near_px` of an own Sapper;
// - a HOSTILE LAY (R2841, v2-the-deep.md §11.8): a new row of another
//   faction that a delta carries inside `lay_near_px` of a Sapper contact
//   of that faction, when the row is unarmed and not inert. The lay makes that Sapper loud for the loud lay of the rules,
//   so the store counts its loud lay from the frame of the lay;
// - a COLLECT: own Sapper rows whose stow grew on the frame that some rows
//   inside `collect_near_px` of it left the picture;
// - a DETONATION: an armed sea mine that left the picture while a hull of
//   the frame stood inside its contact radius, and no collect took it.
//
// Everything here is pure data, so a test reads it with no engine. While
// the flag `sapper` is off the wire carries no row, and the store stays
// empty.

import {
  SeaObjectKind, SeaObjectRules, ShipClass, type Catalog, type FleetState, type SeaObject,
  type SeaObjectSpec, type SeaObjectStock, type State,
} from './gen/proto/aquaphobia';
import { SEA_OBJECTS } from './gen/presentation';
import { MPX_PER_PX } from './world';

/** One sea object of the picture. */
export interface SeaObjectRow {
  id: number;
  faction: number;
  kind: SeaObjectKind;
  /** The point, world px. */
  x: number;
  y: number;
  /** The heading of the Sapper at the lay, radians: a line lies across it. */
  heading: number;
  /** A sea mine: armed. A deployable: its anchor holds and it acts. */
  armed: boolean;
  hullPct: number;
  own: boolean;
  /** A target of a gun of this viewer (`SeaObject.target`, R2894): the
   *  map draws the target bracket round it. Never set on an own row or a
   *  heard row. */
  target: boolean;
  /** A sea mine that a hull at sea alone hears (`SeaObject.heard`, R2902):
   *  the kind reads `SEA_OBJECT_KIND_NONE`, and the map draws the generic
   *  mine mark. */
  heard: boolean;
  /** An own sea mine: the map seconds of its life the wire stated
   *  (R2697), and the wall ms the figure arrived. */
  leftSec: number;
  leftAtMs: number;
  /** The wall ms the row came into the picture, and the wall ms it armed
   *  (0 while unarmed, or when it came into the picture armed). */
  seenMs: number;
  armedMs: number;
  /** The wall ms an armed row turned unarmed again: a sea mine that drifted
   *  into harbour limits (§11.2 rule 5). 0 otherwise. */
  disarmedMs: number;
  /** A sea mine in harbour limits, as the wire states it: unarmed for
   *  good (§11.2 rule 5, R2737). */
  inert: boolean;
  /** A sea mine whose arm window runs (R2737): the map ms to the arm the
   *  wire stated, 0 while the layer is near, and the wall ms the figure
   *  arrived. `armShare` counts it down. */
  armLeftMs: number;
  armAtMs: number;
}

/**
 * The share of the arm window of a sea mine that passed, 0 to 1 (§11.2
 * rule 2, R2737): the wire's map ms to the arm, counted down at
 * `mapPerWall` from the frame that stated it, over `arm_after_sec` of the
 * rules. 1 for an armed row; -1 while no window runs (the layer is near,
 * the row is inert or heard, or the row is no sea mine).
 */
export function armShare(r: Pick<SeaObjectRow, 'armed' | 'armLeftMs' | 'armAtMs'>, nowMs: number,
  mapPerWall: number, armAfterSec = seaRules().armAfterSec): number {
  if (r.armed) return 1;
  if (!(r.armLeftMs > 0)) return -1;
  const leftMs = r.armLeftMs - (nowMs - r.armAtMs) * (mapPerWall > 0 ? mapPerWall : 1);
  if (!(armAfterSec > 0)) return leftMs > 0 ? 0 : 1;
  return Math.max(0, Math.min(1, 1 - leftMs / (armAfterSec * 1000)));
}

/** The wall ms the arm window of a row opened, backed out of the first
 *  figure the wire stated; 0 while no window runs (R2737). */
export function armStartMs(r: Pick<SeaObjectRow, 'armed' | 'armLeftMs' | 'armAtMs'>, mapPerWall: number,
  armAfterSec = seaRules().armAfterSec): number {
  if (!(r.armLeftMs > 0)) return 0;
  const passedMap = Math.max(0, armAfterSec * 1000 - r.armLeftMs);
  return r.armAtMs - passedMap / (mapPerWall > 0 ? mapPerWall : 1);
}

/** The Sapper fields of one own Sapper (R2654). */
export interface SapperRow {
  id: number;
  stow: readonly SeaObjectStock[];
  /** The stow in cargo units is the caller's: the store keeps the counts. */
  laySec: number;
  layAtMs: number;
  loudSec: number;
  loudAtMs: number;
  /** The point at the last frame, world px. */
  x: number;
  y: number;
  heading: number;
}

/** A lay the store saw: the new row and the own Sapper that laid it. */
export interface SeaLay {
  row: SeaObjectRow;
  sapper: number;
  atMs: number;
}

/** A collect the store saw: the own Sapper and the rows it took aboard. */
export interface SeaCollect {
  sapper: number;
  rows: readonly SeaObjectRow[];
  /** The stow before the take. */
  stow0: readonly SeaObjectStock[];
  atMs: number;
}

/** A detonation the store saw: the sea mine and the hull that set it off. */
export interface SeaDetonation {
  row: SeaObjectRow;
  /** The struck hull's point, world px, or null when a round set it off. */
  hit: { x: number; y: number } | null;
  /** The gun hit that set it off (R3031); absent when a hull or a round did. */
  gun?: SeaGunHit;
  atMs: number;
}

/** One gun hit on a sea object (R3031): the point of the shooter, world
 *  px, and the wall seconds until the first round of its salvo arrives. */
export interface SeaGunHit {
  x: number;
  y: number;
  waitSec: number;
}

/** A Sapper of another faction in the picture, and the wall ms the store
 *  last saw it lay (0 for none): its loud lay runs from there (§11.1). */
export interface FoeSapperRow {
  id: number;
  faction: number;
  x: number;
  y: number;
  loudAtMs: number;
}

/** What one State frame changed. */
export interface SeaObjectDelta {
  lays: SeaLay[];
  /** The lays of Sapper contacts of another faction; `sapper` is the
   *  contact. */
  foeLays: SeaLay[];
  collects: SeaCollect[];
  detonations: SeaDetonation[];
}

/** The figures the inference reads, world px: the pack's figures of the
 *  hello catalog and the slack of the presentation lane. */
export interface SeaInference {
  /** A new own row this near an own Sapper is its lay. */
  layNearPx: number;
  /** A row that left this near an own Sapper whose stow grew is aboard. */
  collectNearPx: number;
  /** The contact radius of a sea mine kind, world px. */
  contactPx(kind: SeaObjectKind): number;
}

/** The point of one hull of the frame, as the inference reads it. */
export interface SeaHull { x: number; y: number }

const NO_STOW: readonly SeaObjectStock[] = [];

/** The rules before a hello with `sapper` latched: every figure 0. */
const NO_SEA_RULES: SeaObjectRules = SeaObjectRules.fromPartial({});

// The pack figures of the sea objects, from the hello catalog (R2659): the
// rows of `Catalog.sea_objects` by kind and `CatalogRules.sea_objects`.
// The hub sends both only while the world latched `sapper`, and a hello
// replaces both, as `setBanners` of `world.ts` replaces the banners.
let seaKinds: (SeaObjectSpec | undefined)[] = [];
let seaRulesV: SeaObjectRules = NO_SEA_RULES;

/** Takes the sea object figures of a hello catalog (R2659). A catalog
 *  with no rows, or none at all, clears them. `GameState.setHello` calls it. */
export function setSeaFigures(catalog: Catalog | null | undefined): void {
  seaKinds = [];
  for (const k of catalog?.seaObjects ?? []) seaKinds[k.id] = k;
  seaRulesV = catalog?.rules?.seaObjects ?? NO_SEA_RULES;
}

/** The pack figures of one kind (`SeaObjectSpec` of the hello catalog), or
 *  undefined for `SEA_OBJECT_KIND_NONE` and a kind the hello did not send. */
export function seaKindFigures(kind: SeaObjectKind): SeaObjectSpec | undefined {
  return kind > 0 ? seaKinds[kind] : undefined;
}

/** The pack rules of the sea objects (`CatalogRules.sea_objects`): every
 *  figure 0 before a hello of a world with `sapper` latched. */
export function seaRules(): SeaObjectRules { return seaRulesV; }

/** The figures of the inference, from the hello catalog and the
 *  presentation lane. */
export const SEA_INFERENCE: SeaInference = {
  layNearPx: SEA_OBJECTS.layNearPx,
  get collectNearPx(): number { return seaRulesV.collectPx + SEA_OBJECTS.collectSlackPx; },
  contactPx(kind: SeaObjectKind): number {
    const f = seaKindFigures(kind);
    if (!f) return 0;
    return Math.max(f.triggerPx, f.hearPx * SEA_OBJECTS.sirenReachMul);
  },
};

/** The cargo-free count of a stow: the number of items aboard. */
export function stowCount(stow: readonly SeaObjectStock[]): number {
  let n = 0;
  for (const s of stow) n += s.count;
  return n;
}

/** True for the four sea mine kinds of §11.2. */
export function isSeaMineKind(k: SeaObjectKind): boolean {
  return k >= SeaObjectKind.SEA_OBJECT_KIND_BARB && k <= SeaObjectKind.SEA_OBJECT_KIND_PETARD;
}

export class SeaObjectStore {
  private readonly rowsV = new Map<number, SeaObjectRow>();
  private listV: SeaObjectRow[] = [];
  private listStale = false;
  private readonly sappers = new Map<number, SapperRow>();
  /** The Sapper contacts of another faction. */
  private readonly foes = new Map<number, FoeSapperRow>();
  /** The viewer's faction, at the last frame. */
  private viewer = 0;
  /** The stow totals of the own Sappers that the last frame changed. */
  private readonly stowGrew = new Set<number>();
  /** The gun hits of the events of this tick, by sea object (R3031). The
   *  events of a tick reach the client before its State frame, so the
   *  frame that drops a row reads the hit that ended it. */
  private readonly gunHits = new Map<number, SeaGunHit>();
  private revV = 0;
  /** Map seconds per wall second, at the last frame. */
  private mapPerWall = 1;

  /** A counter that moves on every change of a row, so a reader packs again. */
  get rev(): number { return this.revV; }

  /** The row of an id, or undefined. */
  row(id: number): SeaObjectRow | undefined { return this.rowsV.get(id); }

  /**
   * Notes the gun hit of one HIT event on the sea object `id` (R3031) and
   * answers the row it struck, or undefined for a row the store does not
   * hold. The next State frame reads the note: an armed sea mine that it
   * drops went off on the hit, and struck no hull.
   */
  noteGunHit(id: number, hit: SeaGunHit): SeaObjectRow | undefined {
    const r = this.rowsV.get(id);
    if (r) this.gunHits.set(id, hit);
    return r;
  }

  /** Every row, in id order. */
  get list(): readonly SeaObjectRow[] {
    if (this.listStale) {
      this.listV = [...this.rowsV.values()].sort((a, b) => a.id - b.id);
      this.listStale = false;
    }
    return this.listV;
  }

  /** The Sapper fields of an own Sapper, or undefined. */
  sapper(id: number): SapperRow | undefined { return this.sappers.get(id); }

  /** Every own Sapper the store holds, in fleet id order. */
  get ownSappers(): SapperRow[] {
    return [...this.sappers.values()].sort((a, b) => a.id - b.id);
  }

  /** The map seconds until the next LAY of an own Sapper, counted down. */
  layLeftSec(id: number, nowMs: number): number {
    const s = this.sappers.get(id);
    return s ? left(s.laySec, s.layAtMs, nowMs, this.mapPerWall) : 0;
  }

  /** The map seconds the loud lay of an own Sapper still runs. */
  loudLeftSec(id: number, nowMs: number): number {
    const s = this.sappers.get(id);
    return s ? left(s.loudSec, s.loudAtMs, nowMs, this.mapPerWall) : 0;
  }

  /** The map seconds the loud lay of a Sapper contact of another faction
   *  still runs, from the lay the store saw (`layNoiseSec` of the rules);
   *  0 when it saw none, and once the contact left the picture. */
  foeLoudLeftSec(id: number, nowMs: number): number {
    const s = this.foes.get(id);
    return s && s.loudAtMs > 0 ? left(seaRulesV.layNoiseSec, s.loudAtMs, nowMs, this.mapPerWall) : 0;
  }

  /**
   * The share of the anchor time of a deployable that passed, 0 to 1
   * (v2-the-deep.md §11.3 rule 7, R2723): the map seconds since the row came
   * into the picture over `anchor_sec` of the rules (`palisade_anchor_sec`
   * for the Palisade). An own deployable comes into the picture at its lay,
   * so the share is its anchor; a row that came in later reads a share too
   * low, and the wire states `armed` when the anchor holds. 1 for an armed
   * row and where the rules state no anchor time.
   */
  anchorFrac(r: SeaObjectRow, nowMs: number): number {
    if (r.armed) return 1;
    const rules = seaRules();
    const sec = r.kind === SeaObjectKind.SEA_OBJECT_KIND_PALISADE ? rules.palisadeAnchorSec : rules.anchorSec;
    if (!(sec > 0)) return 1;
    return Math.max(0, Math.min(1, (((nowMs - r.seenMs) / 1000) * this.mapPerWall) / sec));
  }

  /** The seconds of life of an own sea mine, counted down on the map
   *  clock (R2697). */
  lifeLeftSec(r: SeaObjectRow, nowMs: number): number {
    return r.leftSec > 0 ? left(r.leftSec, r.leftAtMs, nowMs, this.mapPerWall) : 0;
  }

  /** Map seconds per wall second, at the last frame. */
  get tactical(): number { return this.mapPerWall; }

  /** The share of the arm window of a row that passed (`armShare`). */
  armFrac(r: SeaObjectRow, nowMs: number): number { return armShare(r, nowMs, this.mapPerWall); }

  /** Every row as the cards read it (`SeaObjectView` of
   *  `ui/cards/seaobjects.ts`): the life of an own sea mine counted down to
   *  `nowMs`. The water peek asks it on a peek, never each frame. */
  views(nowMs: number): (SeaObjectRow & { leftSec: number })[] {
    return this.list.map((r) => ({ ...r, leftSec: Math.ceil(this.lifeLeftSec(r, nowMs)) }));
  }

  /** Forgets everything: a hello is a new world. */
  reset(): void {
    if (this.rowsV.size === 0 && this.sappers.size === 0 && this.foes.size === 0) return;
    this.rowsV.clear();
    this.sappers.clear();
    this.foes.clear();
    this.listStale = true;
    this.revV++;
  }

  /**
   * The Sapper fields of the own Sappers of one State frame. A frame is a
   * delta: a fleet it carries is set again, a removed id goes, and a full
   * frame replaces the set. Call it before `applyState` of the same frame,
   * so a collect reads the stow of this frame. It keeps the Sapper contacts
   * of a faction other than `viewer` too; a full frame keeps the loud lay
   * of a contact it restates.
   */
  observeFleets(fleets: readonly FleetState[], removed: readonly number[], full: boolean,
    nowMs: number, mapPerWall: number, viewer = 0): void {
    this.mapPerWall = mapPerWall > 0 ? mapPerWall : 1;
    this.viewer = viewer;
    this.stowGrew.clear();
    if (full) this.sappers.clear();
    const kept = full && this.foes.size > 0 ? new Map(this.foes) : null;
    if (full) this.foes.clear();
    for (const id of removed) { this.sappers.delete(id); this.foes.delete(id); }
    for (const f of fleets) {
      if (!f.own && f.cls === ShipClass.SHIP_CLASS_SAPPER && f.faction !== viewer) {
        const had = this.foes.get(f.id) ?? kept?.get(f.id);
        this.foes.set(f.id, {
          id: f.id, faction: f.faction, x: f.xMpx / MPX_PER_PX, y: f.yMpx / MPX_PER_PX, loudAtMs: had?.loudAtMs ?? 0,
        });
        continue;
      }
      this.foes.delete(f.id);
      if (!f.own || f.cls !== ShipClass.SHIP_CLASS_SAPPER) { this.sappers.delete(f.id); continue; }
      const stow = f.stow && f.stow.length > 0 ? f.stow.map((s) => ({ ...s })) : NO_STOW;
      const had = this.sappers.get(f.id);
      const x = f.xMpx / MPX_PER_PX, y = f.yMpx / MPX_PER_PX, heading = (f.headingMrad ?? 0) / 1000;
      if (!had) {
        this.sappers.set(f.id, {
          id: f.id, stow, laySec: f.layReadySec ?? 0, layAtMs: nowMs,
          loudSec: f.loudLeftSec ?? 0, loudAtMs: nowMs, x, y, heading,
        });
        continue;
      }
      if (!full && stowCount(stow) > stowCount(had.stow)) this.stowGrew.add(f.id);
      had.stow = stow; had.x = x; had.y = y; had.heading = heading;
      const lay = f.layReadySec ?? 0, loud = f.loudLeftSec ?? 0;
      if (lay !== had.laySec) { had.laySec = lay; had.layAtMs = nowMs; }
      if (loud !== had.loudSec) { had.loudSec = loud; had.loudAtMs = nowMs; }
    }
  }

  /**
   * Takes the sea objects of one State frame and answers what it changed,
   * or null when the frame changed no row. `hulls` are the hulls the frame
   * leaves on the picture, for the detonation test; `fig` the figures of
   * the inference.
   */
  applyState(s: Pick<State, 'full' | 'objects' | 'objectsGone'>, nowMs: number,
    hulls: readonly SeaHull[], fig: SeaInference): SeaObjectDelta | null {
    const rows = s.objects ?? [];
    const gone = s.objectsGone ?? [];
    // The gun hits belong to the tick of this frame; a hit on a row that
    // lives on is spent, and a frame the client missed spends them too.
    const gunHits = this.gunHits.size > 0 ? new Map(this.gunHits) : null;
    this.gunHits.clear();
    if (s.full) {
      if (rows.length === 0 && this.rowsV.size === 0) return null;
      const kept = new Map(this.rowsV);
      this.rowsV.clear();
      for (const w of rows) this.put(w, nowMs, kept.get(w.id));
      this.listStale = true;
      this.revV++;
      return null;
    }
    if (rows.length === 0 && gone.length === 0) return null;
    const delta: SeaObjectDelta = { lays: [], foeLays: [], collects: [], detonations: [] };
    for (const w of rows) {
      const had = this.rowsV.get(w.id);
      const r = this.put(w, nowMs, had);
      if (had) continue;
      if (r.own) {
        const by = nearestSapper(this.sappers, r.x, r.y, fig.layNearPx);
        if (by) delta.lays.push({ row: r, sapper: by.id, atMs: nowMs });
      } else if (r.faction !== this.viewer && !r.armed && !r.inert) {
        const by = nearestSapper(this.foes, r.x, r.y, fig.layNearPx, r.faction);
        if (by) {
          by.loudAtMs = nowMs;
          delta.foeLays.push({ row: r, sapper: by.id, atMs: nowMs });
        }
      }
    }
    const left: SeaObjectRow[] = [];
    for (const id of gone) {
      const r = this.rowsV.get(id);
      if (!r) continue;
      this.rowsV.delete(id);
      left.push(r);
    }
    this.takeCollects(left, nowMs, fig, delta);
    for (const r of left) {
      if (!r.armed || !isSeaMineKind(r.kind)) continue;
      const gun = gunHits?.get(r.id);
      if (gun) {
        // A gun hit set it off (R3031): no hull struck it.
        delta.detonations.push({ row: r, hit: null, gun, atMs: nowMs });
        continue;
      }
      const reach = fig.contactPx(r.kind);
      let hit: SeaHull | null = null;
      let best = Infinity;
      for (const h of hulls) {
        const d = Math.hypot(h.x - r.x, h.y - r.y);
        if (d <= reach && d < best) { best = d; hit = h; }
      }
      if (hit) delta.detonations.push({ row: r, hit: { x: hit.x, y: hit.y }, atMs: nowMs });
    }
    this.listStale = true;
    this.revV++;
    return delta;
  }

  /** The rows that left near an own Sapper whose stow grew: its collect.
   *  They leave `left`, so no detonation reads them. */
  private takeCollects(left: SeaObjectRow[], nowMs: number, fig: SeaInference, delta: SeaObjectDelta): void {
    if (this.stowGrew.size === 0 || left.length === 0) return;
    for (const id of [...this.stowGrew].sort((a, b) => a - b)) {
      const sp = this.sappers.get(id);
      if (!sp) continue;
      const took: SeaObjectRow[] = [];
      for (let i = left.length - 1; i >= 0; i--) {
        const r = left[i];
        if (Math.hypot(r.x - sp.x, r.y - sp.y) > fig.collectNearPx) continue;
        took.push(r);
        left.splice(i, 1);
      }
      if (took.length === 0) continue;
      took.sort((a, b) => a.id - b.id);
      delta.collects.push({ sapper: id, rows: took, stow0: stowBefore(sp.stow, took), atMs: nowMs });
    }
  }

  private put(w: SeaObject, nowMs: number, had: SeaObjectRow | undefined): SeaObjectRow {
    const armed = w.armed;
    const r: SeaObjectRow = had ?? {
      id: w.id, faction: 0, kind: SeaObjectKind.SEA_OBJECT_KIND_NONE, x: 0, y: 0, heading: 0,
      armed, hullPct: 100, own: false, target: false, heard: false, leftSec: 0, leftAtMs: nowMs,
      seenMs: nowMs, armedMs: 0, disarmedMs: 0, inert: false, armLeftMs: 0, armAtMs: nowMs,
    };
    // A heard row states no arm, so a change of grade is no arm and no disarm (R2902).
    if (had && !had.heard && !w.heard) {
      if (armed && !had.armed) { r.armedMs = nowMs; r.disarmedMs = 0; }
      if (!armed && had.armed) r.disarmedMs = nowMs;
    }
    r.faction = w.faction;
    r.kind = w.heard ? SeaObjectKind.SEA_OBJECT_KIND_NONE : w.kind;
    r.x = w.xMpx / MPX_PER_PX;
    r.y = w.yMpx / MPX_PER_PX;
    r.heading = w.headingMrad / 1000;
    r.armed = armed;
    r.hullPct = w.heard ? 100 : w.hullPct;
    r.own = w.own;
    r.heard = w.heard && !w.own;
    r.target = w.target && !w.own && !r.heard;
    r.inert = !r.heard && w.inert;
    const armLeft = r.heard ? 0 : w.armLeftMapMs;
    if (armLeft !== r.armLeftMs || !had) { r.armLeftMs = armLeft; r.armAtMs = nowMs; }
    if (w.leftMapSec !== r.leftSec || !had) { r.leftSec = w.leftMapSec; r.leftAtMs = nowMs; }
    this.rowsV.set(w.id, r);
    return r;
  }
}

/**
 * The Sapper of `rows` nearest a point within `reach`, the lower id on a
 * tie, or null. `faction` keeps the rows of that faction alone.
 */
function nearestSapper<T extends { id: number; x: number; y: number; faction?: number }>(
  rows: ReadonlyMap<number, T>, x: number, y: number, reach: number, faction?: number,
): T | null {
  let best: T | null = null;
  let bd = Infinity;
  for (const s of rows.values()) {
    if (faction !== undefined && s.faction !== faction) continue;
    const d = Math.hypot(s.x - x, s.y - y);
    if (d <= reach && (d < bd || (d === bd && best !== null && s.id < best.id))) { best = s; bd = d; }
  }
  return best;
}

/** The stow before a take: the stow now, less the kinds that came aboard. */
function stowBefore(stow: readonly SeaObjectStock[], took: readonly SeaObjectRow[]): SeaObjectStock[] {
  const out = stow.map((s) => ({ ...s }));
  for (const r of took) {
    const s = out.find((x) => x.kind === r.kind);
    if (s && s.count > 0) s.count--;
  }
  return out.filter((s) => s.count > 0);
}

/** A figure the wire stated at `atMs`, counted down at `perWall`. */
function left(sec: number, atMs: number, nowMs: number, perWall: number): number {
  if (!(sec > 0)) return 0;
  return Math.max(0, sec - ((nowMs - atMs) / 1000) * perWall);
}
